import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue'
  import Question from '@/components/common/Question.vue'
  import Pagination from '@/components/common/Pagination.vue'
  import KetchUpButton from './KetchUpButton.vue'
  import LoadingSpinner from './LoadingSpinner.vue'
  import { CourseModule } from '@/store/modules/course'
  import useCourse from '@/composables/useCourse'
  import useIcons from '@/composables/useIcons'
  import HTMLRenderer from '@/components/common/HTMLRenderer.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import Feedback from '@/components/common/Feedback.vue'
  import eventBus from '@/main'
  import useI18n from '@/composables/useI18n'
  import type { Question as IQuestion, QuestionAnswer } from '@/services/interfaces/QuizQuestion'
  import type { PropType } from 'vue'
  import type { CourseExamQuestion } from '@/services/interfaces/Course'
  import useCommonMixin from '@/composables/useCommonMixin'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'QuestionWithButtons',
  props: {
    questions: {
      required: true,
      type: Array as PropType<(IQuestion | CourseExamQuestion)[]>,
    },
    submitButtonText: {
      type: String,
      default: '',
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
    showAbortButton: {
      type: Boolean,
      default: false,
    },
    showPreviousButton: {
      type: Boolean,
      default: true,
    },
    showNextButton: {
      type: Boolean,
      default: true,
    },
    showButtonSlot: {
      type: Boolean,
      default: false,
    },
    showHelperText: {
      type: Boolean,
      default: true,
    },
    showTransition: {
      type: Boolean,
      default: true,
    },
    showPagination: {
      type: Boolean,
      default: false,
    },
    inResultsMode: {
      type: Boolean,
      default: false,
    },
    showQuestionNumber: {
      type: Boolean,
      default: true,
    },
    currentQuestionNumber: {
      type: Number,
      default: null,
    },
    totalQuestions: {
      type: Number,
      default: null,
    },
    isGuidedQuiz: {
      type: Boolean,
      default: false,
    },
    retainOnlyCurrentQuestionAnswer: {
      type: Boolean,
      default: true,
    },
    parentCourseId: {
      type: String,
      default: '',
    },
    showNextWrongQuestionButton: {
      type: Boolean,
      default: false,
    },
    showSkipQuestionButton: {
      type: Boolean,
      default: false,
    },
    hasNewQuestions: {
      type: Boolean,
      default: false,
    },
    isEmbeddedGuidedQuiz: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['number-answer', 'submit', 'previous', 'next-wrong-question', 'abort', 'skip-question'],
  setup(__props, { emit }) {

const props = __props;

  

  

  const { quizIcon } = useIcons()
  const { openLessonModal } = useCourse()
  const { translateString } = useI18n()
  const { isKnowledgeCheckCompany } = useCommonMixin()

  const currentQuestionIndex = ref(0)
  const selectedAnswers = ref({} as { [key: string]: QuestionAnswer })

  const totalCorrectPercentage = computed(() => {
    return parseFloat(Number(currentQuestion.value.correctAttemptsPercentage).toFixed(2))
  })

  const postingAnswers = ref(false)
  const fetchingPrevQuestion = ref(false)
  const loadingNextWrongQuestion = ref(false)
  const loadingFromSkippedQuestion = ref(false)
  const currentQuestion = computed(() => props.questions[currentQuestionIndex.value])

  const questionId = computed(() => currentQuestion.value.questionId)

  const questionNumber = computed(() => props.currentQuestionNumber || currentQuestionIndex.value + 1)

  const transitionName = computed(() =>
    props.showTransition ? (pageDirection.value === 'next' ? 'next-slide' : 'prev-slide') : '',
  )

  const setAnswers = () => {
    props.questions?.forEach((question) => {
      const selectedAnswerIds: string[] = []
      if (question.questionType === 'number' && question.userNumberAnswer) {
        if (typeof question.userNumberAnswer === 'object') {
          selectedAnswerIds.push(question.userNumberAnswer!.answer)
        } else if (question.userNumberAnswer) {
          selectedAnswerIds.push(question.userNumberAnswer)
        }
      } else if (['multiple', 'single'].includes(question.questionType)) {
        question.answers?.forEach((a) => {
          if (a.selected) {
            selectedAnswerIds.push(a.answerId)
          }
        })
      }
      if (selectedAnswerIds.length) {
        const payload: any = {
          questionId: question.questionId,
          selectedAnswerIds,
        }
        if (question.questionType === 'number' && question.userNumberAnswer) {
          if (typeof question.userNumberAnswer === 'object') {
            payload.numberAnswer = question.userNumberAnswer!.answer
          } else {
            payload.numberAnswer = question.userNumberAnswer
          }
        }
        eventBus.$set(selectedAnswers.value, question.questionId, payload)
      }
    })
  }

  setAnswers()

  const onChoiceClick = (answerIdOrNumberAnswer: string) => {
    if (props.inResultsMode) return
    const questionAnswer = selectedAnswers.value[questionId.value]

    // if reactivity has been established
    if (questionAnswer) {
      if (['single', 'number'].includes(currentQuestion.value.questionType)) {
        questionAnswer.selectedAnswerIds.splice(0, 1, answerIdOrNumberAnswer)
        if (currentQuestion.value.questionType === 'number') questionAnswer.numberAnswer = answerIdOrNumberAnswer
      } else {
        addOrRemoveMultipleChoiceAnswer(questionAnswer, answerIdOrNumberAnswer)
      }
    } else {
      const payload: any = {
        questionId: questionId.value,
        selectedAnswerIds: [answerIdOrNumberAnswer],
      }
      if (currentQuestion.value.questionType === 'number') {
        payload.numberAnswer = answerIdOrNumberAnswer
        emit('number-answer', answerIdOrNumberAnswer)
      }
      eventBus.$set(selectedAnswers.value, questionId.value, payload)
    }
  }

  const addOrRemoveMultipleChoiceAnswer = (questionAnswer: QuestionAnswer, answerId: string) => {
    const index = questionAnswer.selectedAnswerIds.indexOf(answerId)
    if (index > -1) {
      questionAnswer.selectedAnswerIds.splice(index, 1)
    } else {
      questionAnswer.selectedAnswerIds.push(answerId)
    }
  }

  const isChoiceSelected = computed(() => {
    return (answerId: string) => {
      return selectedAnswers.value[questionId.value]?.selectedAnswerIds.includes(answerId) || false
    }
  })

  const questionAnswered = computed(() => !!selectedAnswers.value[questionId.value]?.selectedAnswerIds.length)

  const isLastQuestion = computed(() => currentQuestionIndex.value === props.questions.length - 1)

  const pageDirection = ref('next')
  const onNextPage = () => {
    if (postingAnswers.value) return
    if (!questionAnswered.value) {
      eventBus.$toasted.error(translateString('courses.selectAnOption'))
      return
    }
    pageDirection.value = 'next'
    if (isLastQuestion.value) {
      postingAnswers.value = true
      emit('submit', Object.values(selectedAnswers.value))
      return
    }
    currentQuestionIndex.value += 1
  }

  const onPrevPage = () => {
    if (questionNumber.value === 1) return
    pageDirection.value = 'prev'
    fetchingPrevQuestion.value = true

    if (props.currentQuestionNumber) {
      emit('previous')
      return
    }
    currentQuestionIndex.value -= 1
  }

  const onNextWrongQuestion = () => {
    loadingNextWrongQuestion.value = true
    pageDirection.value = 'next'
    emit('next-wrong-question')
  }

  const onSkipQuestion = () => {
    loadingFromSkippedQuestion.value = true
    pageDirection.value = 'next'
    emit('skip-question')
  }

  const resetLoaders = () => {
    if (fetchingPrevQuestion.value) {
      fetchingPrevQuestion.value = false
    }
    if (postingAnswers.value) {
      postingAnswers.value = false
    }
    if (loadingNextWrongQuestion.value) {
      loadingNextWrongQuestion.value = false
    }
  }

  watch(
    () => props.inResultsMode,
    () => {
      if (postingAnswers.value && props.isGuidedQuiz) {
        postingAnswers.value = false
      }
    },
  )

  watch(questionId, (newQuestionId: string, oldQuestionId: string) => {
    if (props.retainOnlyCurrentQuestionAnswer) {
      setAnswers()
      if (oldQuestionId) eventBus.$delete(selectedAnswers.value, oldQuestionId)
    }
    if (loadingFromSkippedQuestion.value) {
      loadingFromSkippedQuestion.value = false
    }
  })

  watch(questionNumber, () => {
    resetLoaders()
    if (props.inResultsMode && !props.isGuidedQuiz) setAnswers()
  })

  watch(currentQuestion, () => {
    resetLoaders()
  })

  const helperText = computed(() => {
    if (!props.showHelperText) return ''
    if (currentQuestion.value.questionType === 'number') {
      return translateString('quiz.questionQuizModal.numberQuestionType')
    }
    if (currentQuestion.value.questionType === 'single') {
      return translateString('quiz.questionQuizModal.singleQuestionType')
    }
    return translateString('quiz.questionQuizModal.multipleQuestionType')
  })

  const { currentCourseId } = useCourse()
  const showLessonModal = () => {
    const lessonId = currentQuestion.value?.lesson?.id
    const _courseId = props.parentCourseId || currentCourseId.value
    const moduleId = CourseModule.lessonModule(
      props.parentCourseId || currentCourseId.value,
      currentQuestion.value!.lesson!.id,
    )?.id
    if (lessonId && _courseId && moduleId) {
      openLessonModal({
        lessonId,
        courseId: _courseId,
        moduleId,
      })
    }
  }

return { __sfc: true,props, emit, quizIcon, openLessonModal, translateString, isKnowledgeCheckCompany, currentQuestionIndex, selectedAnswers, totalCorrectPercentage, postingAnswers, fetchingPrevQuestion, loadingNextWrongQuestion, loadingFromSkippedQuestion, currentQuestion, questionId, questionNumber, transitionName, setAnswers, onChoiceClick, addOrRemoveMultipleChoiceAnswer, isChoiceSelected, questionAnswered, isLastQuestion, pageDirection, onNextPage, onPrevPage, onNextWrongQuestion, onSkipQuestion, resetLoaders, helperText, currentCourseId, showLessonModal, computed, ref, watch, Question, Pagination, KetchUpButton, LoadingSpinner, CourseModule, useCourse, useIcons, HTMLRenderer, SVGRenderer, Feedback, eventBus, useI18n, useCommonMixin }
}

})